import React from "react";
import { CardContent, Box, Typography, Card, Skeleton } from "@mui/material";
import {
  DirectionsCar,
  TwoWheeler,
  AttachMoney,
  ConfirmationNumber,
  Star,
  SentimentVerySatisfied,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
} from "@mui/icons-material";
import { IconBox } from "../common/IconBox";
import { StyledCard } from "../common/StyledCard";
import { FlexBetween, FlexRow } from "../common/FlexBox";
import {
  MetricText,
  SubText,
  CardTitle,
  LabelText,
} from "../common/Typography";
import {
  CardSkeleton,
  HeaderSkeleton,
  ChartSkeleton,
} from "../Skeleton/SkeletonLoaders";

// ============================================================
// 1. GlassMorphismCard - Glass-morphism hero card
// ============================================================

interface GlassMorphismCardProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  isLoading?: boolean;
}

/**
 * GlassMorphismCard - Reusable glass-morphism card for hero sections
 *
 * Features:
 * - Glass-morphism effect (backdrop blur, transparent background)
 * - Smooth hover animations (lift + background change)
 * - Responsive sizing (mobile, tablet, desktop)
 * - Skeleton loading states with white overlay
 * - Professional shadow effects
 *
 * @example
 * <GlassMorphismCard
 *   icon={<ConfirmationNumber />}
 *   value={1234}
 *   label="Total Tickets"
 *   isLoading={false}
 * />
 */
export const GlassMorphismCard: React.FC<GlassMorphismCardProps> = ({
  icon,
  value,
  label,
  isLoading = false,
}) => {
  return (
    <Card
      sx={{
        background: "rgba(255,255,255,0.15)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255,255,255,0.25)",
        borderRadius: 3,
        color: "white",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        "&:hover": {
          transform: "translateY(-4px)",
          background: "rgba(255,255,255,0.22)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ textAlign: "center", p: { xs: 2, sm: 3 } }}>
        {isLoading ? (
          <>
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{
                mx: "auto",
                mb: 1,
                bgcolor: "rgba(255,255,255,0.2)",
              }}
            />
            <Skeleton
              variant="text"
              sx={{
                fontSize: "2rem",
                bgcolor: "rgba(255,255,255,0.2)",
              }}
            />
            <Skeleton
              variant="text"
              sx={{
                fontSize: "0.875rem",
                bgcolor: "rgba(255,255,255,0.2)",
              }}
            />
          </>
        ) : (
          <>
            {React.cloneElement(icon as React.ReactElement, {
              sx: { fontSize: { xs: 32, sm: 40 }, mb: 1 },
            })}
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
            >
              {value}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {label}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================
// 2. VehicleMetricCard - Complete vehicle metric card
// ============================================================

interface VehicleMetricCardProps {
  type: "car" | "bike";
  count: number;
  subtitle?: string;
  isLoading?: boolean;
  gradient?:
    | "purple"
    | "pink"
    | "cyan"
    | "gold"
    | "green"
    | "greenAlt"
    | "redAlt"
    | "purpleAlt"
    | string;
}

export const VehicleMetricCard: React.FC<VehicleMetricCardProps> = ({
  type,
  count,
  subtitle,
  isLoading = false,
  gradient = type === "car" ? "pink" : "cyan",
}) => {
  const icon = type === "car" ? <DirectionsCar /> : <TwoWheeler />;
  const defaultSubtitle =
    type === "car" ? "Four-wheelers parked" : "Two-wheelers parked";

  return (
    <StyledCard hoverable>
      <CardContent sx={{ p: 3 }}>
        {isLoading ? (
          <CardSkeleton iconSize={56} />
        ) : (
          <>
            <FlexBetween mb={2}>
              <IconBox
                icon={icon}
                gradient={gradient}
                size={56}
                iconSize={28}
              />
              <CardTitle color="text.secondary">
                {type === "car" ? "Cars" : "Bikes"}
              </CardTitle>
            </FlexBetween>
            <MetricText value={count} />
            <SubText>{subtitle || defaultSubtitle}</SubText>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

// ============================================================
// 2. RevenueMetricCard - Revenue/money metric card
// ============================================================

interface RevenueMetricCardProps {
  amount: number;
  label: string;
  subtitle?: string;
  currency?: string;
  isLoading?: boolean;
  gradient?:
    | "purple"
    | "pink"
    | "cyan"
    | "gold"
    | "green"
    | "greenAlt"
    | "redAlt"
    | "purpleAlt"
    | string;
}

export const RevenueMetricCard: React.FC<RevenueMetricCardProps> = ({
  amount,
  label,
  subtitle,
  currency = "₹",
  isLoading = false,
  gradient = "gold",
}) => {
  return (
    <StyledCard hoverable>
      <CardContent sx={{ p: 3 }}>
        {isLoading ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Skeleton
                variant="rounded"
                width={56}
                height={56}
                sx={{ borderRadius: 2 }}
              />
              <Skeleton
                variant="text"
                width={120}
                sx={{ fontSize: "1.25rem" }}
              />
            </Box>
            <Skeleton variant="text" sx={{ fontSize: "3rem", mb: 1 }} />
            {subtitle && (
              <Skeleton variant="text" sx={{ fontSize: "0.875rem" }} />
            )}
          </>
        ) : (
          <>
            <FlexBetween mb={2}>
              <IconBox
                icon={<AttachMoney />}
                gradient={gradient}
                size={56}
                iconSize={28}
              />
              <CardTitle color="text.secondary">{label}</CardTitle>
            </FlexBetween>
            <MetricText value={`${currency}${amount.toFixed(2)}`} />
            {subtitle && <SubText>{subtitle}</SubText>}
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

// ============================================================
// 3. TicketMetricCard - Ticket count metric card
// ============================================================

interface TicketMetricCardProps {
  count: number;
  label?: string;
  subtitle?: string;
  isLoading?: boolean;
  gradient?:
    | "purple"
    | "pink"
    | "cyan"
    | "gold"
    | "green"
    | "greenAlt"
    | "redAlt"
    | "purpleAlt"
    | string;
}

export const TicketMetricCard: React.FC<TicketMetricCardProps> = ({
  count,
  label = "Total Tickets",
  subtitle = "Parking tickets issued",
  isLoading = false,
  gradient = "royalBlue",
}) => {
  return (
    <StyledCard hoverable>
      <CardContent sx={{ p: 3 }}>
        {isLoading ? (
          <CardSkeleton iconSize={56} />
        ) : (
          <>
            <FlexBetween mb={2}>
              <IconBox
                icon={<ConfirmationNumber />}
                gradient={gradient}
                size={56}
                iconSize={28}
              />
              <CardTitle color="text.secondary">{label}</CardTitle>
            </FlexBetween>
            <MetricText value={count} />
            <SubText>{subtitle}</SubText>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

// ============================================================
// 4. RatingDistributionCard - Complete rating breakdown
// ============================================================

interface RatingData {
  excellent: number;
  good: number;
  average: number;
  poor: number;
}

interface RatingDistributionCardProps {
  ratings: RatingData;
  totalCount: number;
  title?: string;
  isLoading?: boolean;
}

export const RatingDistributionCard: React.FC<RatingDistributionCardProps> = ({
  ratings,
  totalCount,
  title = "Rating Distribution",
  isLoading = false,
}) => {
  const calculatePercentage = (count: number) =>
    totalCount > 0 ? ((count / totalCount) * 100).toFixed(1) : "0.0";

  const ratingConfig = [
    {
      label: "Excellent",
      value: "5/5",
      count: ratings.excellent,
      percentage: calculatePercentage(ratings.excellent),
      color: "#4caf50",
      icon: <SentimentVerySatisfied />,
    },
    {
      label: "Good",
      value: "4/5",
      count: ratings.good,
      percentage: calculatePercentage(ratings.good),
      color: "#2196f3",
      icon: <SentimentSatisfied />,
    },
    {
      label: "Average",
      value: "3/5",
      count: ratings.average,
      percentage: calculatePercentage(ratings.average),
      color: "#ff9800",
      icon: <SentimentNeutral />,
    },
    {
      label: "Poor",
      value: "1-2/5",
      count: ratings.poor,
      percentage: calculatePercentage(ratings.poor),
      color: "#f44336",
      icon: <SentimentDissatisfied />,
    },
  ];

  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        {isLoading ? (
          <HeaderSkeleton iconSize={24} showSubtitle />
        ) : (
          <>
            <FlexRow gap={1} mb={3}>
              <Star color="primary" />
              <CardTitle>{title}</CardTitle>
            </FlexRow>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              {ratingConfig.map((rating) => (
                <Box key={rating.label} sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: rating.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 8px",
                      color: "white",
                    }}
                  >
                    {rating.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={rating.color}
                  >
                    {rating.percentage}%
                  </Typography>
                  <LabelText>{rating.label}</LabelText>
                  <SubText sx={{ fontSize: "0.75rem" }}>
                    {rating.count} reviews
                  </SubText>
                  <Box
                    sx={{
                      height: 6,
                      bgcolor: "grey.200",
                      borderRadius: 3,
                      overflow: "hidden",
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${rating.percentage}%`,
                        bgcolor: rating.color,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

// ============================================================
// 5. ChartCard - Consistent chart container with header
// ============================================================

interface ChartCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
  headerActions?: React.ReactNode; // Dropdown, buttons, etc.
  chartHeight?: number;
}

export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  icon,
  children,
  isLoading = false,
  headerActions,
  chartHeight = 300,
}) => {
  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        {isLoading ? (
          <ChartSkeleton
            height={chartHeight}
            showHeader
            showDropdown={!!headerActions}
          />
        ) : (
          <>
            <FlexBetween mb={3}>
              <FlexRow gap={1}>
                {icon && (
                  <IconBox
                    icon={icon}
                    size={36}
                    iconSize={20}
                    gradient="purple"
                  />
                )}
                <Box>
                  <CardTitle>{title}</CardTitle>
                  {subtitle && <SubText>{subtitle}</SubText>}
                </Box>
              </FlexRow>
              {headerActions && <Box>{headerActions}</Box>}
            </FlexBetween>

            <Box sx={{ width: "100%", height: chartHeight }}>{children}</Box>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

// ============================================================
// 6. StatCard - Simple stat card with optional comparison
// ============================================================

interface StatCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  gradient?:
    | "purple"
    | "pink"
    | "cyan"
    | "gold"
    | "green"
    | "greenAlt"
    | "redAlt"
    | "purpleAlt"
    | string; // Allow custom gradient strings
  valueGradient?: string; // Custom gradient for the value text (e.g., "linear-gradient(45deg, #4caf50, #81c784)")
  change?: {
    value: number;
    label: string;
    isPositive?: boolean;
  };
  isLoading?: boolean;
}

/**
 * StatCard - Simple stat card with icon, value, and optional gradient text
 *
 * Features:
 * - Icon box with gradient background
 * - Large metric value (with optional gradient text effect)
 * - Subtitle text
 * - Optional change indicator (percentage with label)
 * - Skeleton loading states
 * - Hover effects
 *
 * Enhanced with gradient text support for values (e.g., Peak Times)
 *
 * @example
 * // Basic usage
 * <StatCard
 *   label="Total Feedback"
 *   value={1234}
 *   subtitle="Customer reviews"
 *   icon={<Message />}
 *   gradient="purple"
 *   isLoading={false}
 * />
 *
 * @example
 * // With gradient text value (Peak Times pattern)
 * <StatCard
 *   label="Peak Entry Time"
 *   value="10:30 AM"
 *   subtitle="125 entries"
 *   icon={<TrendingUp />}
 *   gradient="green"
 *   valueGradient="linear-gradient(45deg, #4caf50, #81c784)"
 *   isLoading={false}
 * />
 *
 * @example
 * // With change indicator
 * <StatCard
 *   label="Avg Rating"
 *   value={4.5}
 *   subtitle="Out of 5 stars"
 *   change={{ value: 12.5, label: "vs last week", isPositive: true }}
 * />
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  subtitle,
  icon = <Star />,
  gradient = "royalBlue",
  valueGradient,
  change,
  isLoading = false,
}) => {
  return (
    <StyledCard hoverable>
      <CardContent sx={{ p: 3 }}>
        {isLoading ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Skeleton
                variant="rounded"
                width={56}
                height={56}
                sx={{ borderRadius: 2 }}
              />
              <Skeleton
                variant="text"
                width={120}
                sx={{ fontSize: "1.25rem" }}
              />
            </Box>
            <Skeleton variant="text" sx={{ fontSize: "3rem", mb: 1 }} />
            {subtitle && (
              <Skeleton variant="text" sx={{ fontSize: "0.875rem" }} />
            )}
            {change && (
              <Skeleton
                variant="text"
                width="60%"
                sx={{ fontSize: "0.875rem", mt: 1 }}
              />
            )}
          </>
        ) : (
          <>
            <FlexBetween mb={2}>
              <IconBox
                icon={icon}
                gradient={gradient}
                size={56}
                iconSize={28}
              />
              <CardTitle color="text.secondary">{label}</CardTitle>
            </FlexBetween>

            {/* Gradient text value support */}
            {valueGradient ? (
              <Typography
                variant="h3"
                fontWeight={700}
                gutterBottom
                sx={{
                  background: valueGradient,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: "inline-block",
                }}
              >
                {value}
              </Typography>
            ) : (
              <MetricText value={value} />
            )}

            {subtitle && (
              <SubText sx={{ mb: change ? 1 : 0 }}>{subtitle}</SubText>
            )}

            {change && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: change.isPositive
                    ? "success.lighter"
                    : "error.lighter",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color={change.isPositive ? "success.main" : "error.main"}
                >
                  {change.isPositive ? "+" : ""}
                  {change.value}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {change.label}
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

// ============================================================
// Export all HOC components
// ============================================================

const HigherOrderCards = {
  VehicleMetricCard,
  RevenueMetricCard,
  TicketMetricCard,
  RatingDistributionCard,
  ChartCard,
  StatCard,
};

export default HigherOrderCards;
